import { Injectable, inject } from '@angular/core';
import { ModalController, AlertController } from '@ionic/angular';
import {
  TEmbSentEnhancedTagInfo,
  TEmbLandingViewDescriptor,
  ITagBctx,
  TEmbDefectiveDTOWithQty,
  TEmbReceiveUpdateDispatchedQty,
  TEmbReceiveEnhancedTagInfoWithoutResolvedQuantityInfo,
  TEmbSentCreateItem,
  TEmbSentLogListing,
  TUserPaginationOption,
  TEmbReceiveLogListingDetailItem,
  TPaginationInfo,
  TEmbReceiveEnhancedTagInfo,
  TEmbReceiveDispatchedQty,
  TMultiSelectItem,
  TEmbReceiveCreateItem,
  TEmbReceiveLogListing,
  TEmbSentTagDetails,
  TEmbError,
} from '@monorepo/types';
import { EmbFailureComponent, EmbSuccessComponent } from '@monorepo/ui';
import { TranslateService } from '@ngx-translate/core';
import uniq from 'lodash/uniq';

@Injectable({
  providedIn: 'root',
})
export class EmbellishmentUtilService {
  private readonly MAX_RECORDS_IN_ONE_TRANSACTION: number = 50;
  private readonly modalController = inject(ModalController);
  private readonly alertController = inject(AlertController);
  private readonly translateService = inject(TranslateService);

  mapScannerResult(sData: Record<string, string>, config: TEmbLandingViewDescriptor['scanner_conf']) {
    const infoData: Record<string, string | number> = {};
    const tagMappings = config.tag_mappings;
    const dataMapping = tagMappings['info_mappings'] ? tagMappings['info_mappings'] : [];
    dataMapping.forEach((map) => {
      if (map.type === 'number') {
        infoData[map.info_key] = parseInt(sData[map.tag_key]);
      } else {
        infoData[map.info_key] = sData[map.tag_key];
      }
    });
    return infoData;
  }

  getMaxRecordsPerTransaction(): number {
    return this.MAX_RECORDS_IN_ONE_TRANSACTION;
  }

  getAllKvArrayForEmbApp(
    scannerConf: TEmbLandingViewDescriptor['scanner_conf'],
    decodedQrData: Record<string, string>,
  ): ITagBctx[] {
    const infoData = this.mapScannerResult(decodedQrData, scannerConf);
    const keyValueArray: ITagBctx[] = [];
    Object.keys(infoData).forEach((key) => {
      if (key in infoData) {
        const value = infoData[key];
        const stringValue = typeof value === 'number' ? value.toString() : value;
        const obj: ITagBctx = {
          k: key as ITagBctx['k'],
          v: stringValue,
        };
        keyValueArray.push(obj);
      }
    });
    return keyValueArray;
  }

  getTagQty(tagInfo: ITagBctx[], defaultValue = 0): number {
    const bctx = tagInfo.find((bctx) => bctx.k === 'bundleQuantity');
    return bctx ? parseInt(bctx.v) : defaultValue;
  }

  getTagValueByKey(record: ITagBctx[], key: string, defaultValue = '') {
    const tagIdBctx: ITagBctx | undefined = record.find((bctx) => bctx.k === key);
    return tagIdBctx ? tagIdBctx.v : defaultValue;
  }

  getQuickTagInfo(tagDetails: ITagBctx[]): Record<ITagBctx['k'], string> {
    return tagDetails.reduce((acc, kv) => {
      acc[kv.k] = kv.v;
      return acc;
    }, {} as Record<ITagBctx['k'], string>);
  }

  getEmbSentEnhancedTagInfoFromTagDetails(tagDetails: ITagBctx[]): TEmbSentEnhancedTagInfo {
    return {
      embType: 'EMB_SENT',
      updatedQty: this.getTagQty(tagDetails),
      tagDTO: {
        tagDetails: tagDetails,
        tagId: this.getTagValueByKey(tagDetails, 'displayComponentTag'),
        sentQty: this.getTagQty(tagDetails),
        quickTagInfo: this.getQuickTagInfo(tagDetails),
      },
    };
  }
  getEmbReceiveCurrentMissingQty(enhancedTagInfo: TEmbReceiveEnhancedTagInfoWithoutResolvedQuantityInfo) {
    const dispatchedQty = enhancedTagInfo.tagDTO.dispatchedQty;
    return enhancedTagInfo.tagDTO.sentQty - (dispatchedQty.ftt + dispatchedQty.rectified + dispatchedQty.defectives);
  }

  getEmbReceiveCurrentDispatchQty(enhancedTagInfo: TEmbReceiveEnhancedTagInfoWithoutResolvedQuantityInfo) {
    return enhancedTagInfo.tagDTO.dispatchedQty.ftt + enhancedTagInfo.tagDTO.dispatchedQty.rectified;
  }

  getEmbReceiveUpdatedDispatchQty(enhancedTagInfo: TEmbReceiveEnhancedTagInfoWithoutResolvedQuantityInfo) {
    const dispatchedQty = enhancedTagInfo.tagDTO.dispatchedQty;
    const updatedDispatchedQty = enhancedTagInfo.updatedDispatchedQtyDTO;
    return dispatchedQty.ftt + dispatchedQty.rectified + updatedDispatchedQty.ftt + updatedDispatchedQty.rectified;
  }

  getDefectivesCount(defectives: TEmbDefectiveDTOWithQty[]) {
    const hasPanelNumbers = defectives.some((d) => d.panelNumbers?.length);
    if (hasPanelNumbers) {
      const uniquePanels = new Set<string>();
      defectives.forEach((d) => d.panelNumbers?.forEach((p) => uniquePanels.add(p)));
      return uniquePanels.size;
    }
    return defectives.reduce((acc, curr) => acc + curr.qty, 0);
  }

  getEmbReceiveUpdatedMissingQty(enhancedTagInfo: TEmbReceiveEnhancedTagInfoWithoutResolvedQuantityInfo) {
    const dispatchedQty = enhancedTagInfo.tagDTO.dispatchedQty;
    const updatedDispatchedQty = enhancedTagInfo.updatedDispatchedQtyDTO;
    return (
      enhancedTagInfo.tagDTO.sentQty -
      (dispatchedQty.ftt + dispatchedQty.rectified + dispatchedQty.defectives) -
      updatedDispatchedQty.ftt -
      this.getDefectivesCount(updatedDispatchedQty.defectives)
    );
  }

  updateEmbReceiveResolvedQuantityInfo(enhancedTagInfo: TEmbReceiveEnhancedTagInfo): TEmbReceiveEnhancedTagInfo {
    return {
      ...enhancedTagInfo,
      resolvedQuantityInfo: {
        currentMissingQty: this.getEmbReceiveCurrentMissingQty(enhancedTagInfo),
        currentDispatchQty: this.getEmbReceiveCurrentDispatchQty(enhancedTagInfo),
        updatedDispatchQty: this.getEmbReceiveUpdatedDispatchQty(enhancedTagInfo),
        updatedMissingQty: this.getEmbReceiveUpdatedMissingQty(enhancedTagInfo),
        newDefectivesCount: this.getDefectivesCount(enhancedTagInfo.updatedDispatchedQtyDTO.defectives),
      },
    };
  }

  setEmbReceiveResolvedQuantityInfo(enhancedTagInfo: TEmbReceiveEnhancedTagInfoWithoutResolvedQuantityInfo) {
    return this.updateEmbReceiveResolvedQuantityInfo({
      ...enhancedTagInfo,
      resolvedQuantityInfo: {
        currentMissingQty: 0,
        currentDispatchQty: 0,
        updatedDispatchQty: 0,
        updatedMissingQty: 0,
        newDefectivesCount: 0,
      },
    });
  }

  getDefaultUpdatedDispatchQty(defaultFtt = 0): TEmbReceiveUpdateDispatchedQty {
    return {
      ftt: defaultFtt,
      defectives: [],
      rectified: 0,
    };
  }

  getDefaultDispatchedQty(): TEmbReceiveDispatchedQty {
    return {
      ftt: 0,
      defectives: 0,
      rectified: 0,
    };
  }

  getEmbReceiveEnhancedTagInfoFromSentTagDetails(sentTagDetails: TEmbSentTagDetails, vendorDs: string) {
    return this.setEmbReceiveResolvedQuantityInfo({
      embType: 'EMB_RECEIVED',
      updatedDispatchedQtyDTO: this.getDefaultUpdatedDispatchQty(sentTagDetails.sentQty),
      tagDTO: {
        vendor: sentTagDetails.vendor,
        sentQty: sentTagDetails.sentQty,
        tagDetails: sentTagDetails.tagDetails,
        quickTagInfo: this.getQuickTagInfo(sentTagDetails.tagDetails),
        tagId: sentTagDetails.tagId,
        dispatchedQty: this.getDefaultDispatchedQty(),
        vendorDs: vendorDs,
      },
    });
  }

  getTEmbSentCreateItemFromEnhancedTagInfo(enhancedTagInfo: TEmbSentEnhancedTagInfo): TEmbSentCreateItem {
    return {
      tagId: enhancedTagInfo.tagDTO.tagId,
      sentQty: enhancedTagInfo.updatedQty,
    };
  }

  getTEmbReceiveCreateItemFromEnhancedTagInfo(
    enhancedTagInfo: TEmbReceiveEnhancedTagInfo,
    vendorName: string,
  ): TEmbReceiveCreateItem {
    return {
      vendor: enhancedTagInfo.tagDTO.vendor,
      tagId: enhancedTagInfo.tagDTO.tagId,
      vendorName,
      dispatchedQtyInDTO: {
        ftt: enhancedTagInfo.updatedDispatchedQtyDTO.ftt,
        defectives: enhancedTagInfo.updatedDispatchedQtyDTO.defectives,
      },
    };
  }

  getVendorDisplayName(
    vendorList: string[],
    vendor_enabled: TEmbLandingViewDescriptor['vendor_enabled'],
    default_vendor: TEmbLandingViewDescriptor['default_vendor'],
    default_vendor_list: TMultiSelectItem[],
  ): string {
    if (!vendor_enabled) return default_vendor;
    const uniqueVendorList = uniq(vendorList);
    if (uniqueVendorList.length === 0) return '--';
    const foundVendor = default_vendor_list.find((vendor) => vendor.id === uniqueVendorList[0]);
    const firstVendorName = foundVendor ? foundVendor.name : uniqueVendorList[0];
    return uniqueVendorList.length === 1 ? firstVendorName : firstVendorName + `(+${uniqueVendorList.length - 1} more)`;
  }

  getVenderNameById(venderId: string, vendorList: TMultiSelectItem[]) {
    const vender = vendorList.find((vender) => venderId === vender.id);
    return vender ? vender.name : venderId;
  }

  getEmbReceiveEnhancedTagInfoFromListingDetailItem(
    item: TEmbReceiveLogListingDetailItem,
    vendorDisplayName: string,
  ): TEmbReceiveEnhancedTagInfo {
    return this.setEmbReceiveResolvedQuantityInfo({
      embType: 'EMB_RECEIVED',
      tagDTO: {
        tagDetails: item.tagDetails,
        sentQty: item.sentQty,
        tagId: item.tagId,
        dispatchedQty: item.dispatchedQty,
        quickTagInfo: this.getQuickTagInfo(item.tagDetails),
        vendor: item.vendor,
        vendorDs: vendorDisplayName,
      },
      updatedDispatchedQtyDTO: this.getDefaultUpdatedDispatchQty(),
    });
  }

  setPaginationInfo(
    res: TEmbSentLogListing | TEmbReceiveLogListing,
    userPaginationOption: TUserPaginationOption,
  ): TPaginationInfo {
    const pageSize = userPaginationOption.pageSize;
    const pageNo = userPaginationOption.pageNo;
    const totalItems = res.totalRecords || 0;
    const totalPageCount = Math.ceil(res.totalRecords / pageSize) || 0;
    const paginationInfo: TPaginationInfo = {
      totalPageCount: totalPageCount,
      totalItems: totalItems,
      itemsPerPage: pageSize,
      rangeInPage: {
        start: pageSize * (pageNo - 1) + 1,
        end: pageNo * pageSize > totalItems ? totalItems : pageSize * pageNo,
      },
    };
    return paginationInfo;
  }

  async showEmbSuccessModal(message: string, duration = 1000) {
    const successModal = await this.modalController.create({
      component: EmbSuccessComponent,
      componentProps: {
        message: message,
      },
      backdropDismiss: false,
      keyboardClose: false,
      cssClass: 'm-modal_emb-success',
    });
    await successModal.present();
    const timeout = setTimeout(() => {
      successModal.dismiss();
      clearTimeout(timeout);
    }, duration);
    return successModal.onDidDismiss();
  }

  async showEmbFailureModal(errors: TEmbError[]) {
    const failureModal = await this.modalController.create({
      component: EmbFailureComponent,
      componentProps: {
        errorsObj: errors,
      },
      backdropDismiss: false,
      keyboardClose: false,
      cssClass: 'm-modal_emb-error',
    });
    await failureModal.present();
    return failureModal.onDidDismiss<string[]>();
  }

  async showRemoveConfirmationAlert(tagId: string): Promise<boolean> {
    const alert = await this.alertController.create({
      header: this.translateService.instant('emb_common.warning'),
      subHeader: `${this.translateService.instant('emb_common.are_you_sure_to_remove_tag')} ${tagId}?`,
      buttons: [
        {
          text: this.translateService.instant('emb_common.no'),
          role: 'cancel',
          handler: () => false,
        },
        {
          text: this.translateService.instant('emb_common.yes'),
          handler: () => true,
        },
      ],
    });
    return new Promise((resolve) => {
      alert.onDidDismiss().then((data) => {
        const result = data?.role !== 'cancel';
        resolve(result);
      });
      alert.present();
    });
  }

  /**
   * Displays a retry alert with the provided header and sub-header.
   * @param retryCallback - The function to call when the user chooses to retry.
   * @returns A promise that resolves when the alert is presented.
   */

  async showRetryAlert(
    retryCallback: () => void,
    headerKey = 'emb_common.error',
    subHeaderKey = 'emb_common.something_went_wrong',
  ) {
    const popup = await this.alertController.create({
      header: this.translateService.instant(headerKey),
      subHeader: this.translateService.instant(subHeaderKey),
      buttons: [
        {
          text: this.translateService.instant('emb_common.cancel'),
          role: 'cancel',
        },
        {
          text: this.translateService.instant('emb_common.retry'),
          role: 'confirm',
          handler: retryCallback,
        },
      ],
    });
    await popup.present();
  }
}
